import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from 'react';
import logger from '../utils/logger';

// Initial state for the post form
const initialState = {
  // Modal state
  isEditModalOpen: false,
  isPostModalOpen: false,

  // Form data
  postToEdit: null,
  isEditing: false,
  isPosting: false,

  // Form fields
  songURL: '',
  songName: '',
  songArtist: '',
  description: '',
  selectedGenres: [],

  // UI state
  showFullForm: false,
  isUrlValid: false,
  urlError: '',
  fetchedTitle: '',
  fetchedThumbnail: '',

  // Validation state
  validationErrors: {},
};

// Action types for the reducer
export const POST_FORM_ACTIONS = {
  // Modal actions
  OPEN_EDIT_MODAL: 'OPEN_EDIT_MODAL',
  CLOSE_EDIT_MODAL: 'CLOSE_EDIT_MODAL',
  OPEN_POST_MODAL: 'OPEN_POST_MODAL',
  CLOSE_POST_MODAL: 'CLOSE_POST_MODAL',

  // Form state actions
  SET_POSTING_STATE: 'SET_POSTING_STATE',
  SET_EDITING_STATE: 'SET_EDITING_STATE',

  // Form field actions
  SET_SONG_URL: 'SET_SONG_URL',
  SET_SONG_NAME: 'SET_SONG_NAME',
  SET_SONG_ARTIST: 'SET_SONG_ARTIST',
  SET_DESCRIPTION: 'SET_DESCRIPTION',
  SET_SELECTED_GENRES: 'SET_SELECTED_GENRES',

  // UI state actions
  SET_SHOW_FULL_FORM: 'SET_SHOW_FULL_FORM',
  SET_URL_VALID: 'SET_URL_VALID',
  SET_URL_ERROR: 'SET_URL_ERROR',
  SET_FETCHED_TITLE: 'SET_FETCHED_TITLE',
  SET_FETCHED_THUMBNAIL: 'SET_FETCHED_THUMBNAIL',

  // Validation actions
  SET_VALIDATION_ERRORS: 'SET_VALIDATION_ERRORS',
  CLEAR_VALIDATION_ERRORS: 'CLEAR_VALIDATION_ERRORS',

  // Form reset actions
  RESET_FORM: 'RESET_FORM',
  LOAD_POST_FOR_EDIT: 'LOAD_POST_FOR_EDIT',
};

// Reducer function to handle state updates
const postFormReducer = (state, action) => {
  switch (action.type) {
    case POST_FORM_ACTIONS.OPEN_EDIT_MODAL:
      return {
        ...state,
        isEditModalOpen: true,
        isEditing: true,
        postToEdit: action.payload,
      };

    case POST_FORM_ACTIONS.CLOSE_EDIT_MODAL:
      return {
        ...state,
        isEditModalOpen: false,
        isEditing: false,
        postToEdit: null,
      };

    case POST_FORM_ACTIONS.OPEN_POST_MODAL:
      return {
        ...state,
        isPostModalOpen: true,
        isEditing: false,
        postToEdit: null,
      };

    case POST_FORM_ACTIONS.CLOSE_POST_MODAL:
      return {
        ...state,
        isPostModalOpen: false,
        isEditing: false,
        postToEdit: null,
      };

    case POST_FORM_ACTIONS.SET_POSTING_STATE:
      return {
        ...state,
        isPosting: action.payload,
      };

    case POST_FORM_ACTIONS.SET_EDITING_STATE:
      return {
        ...state,
        isEditing: action.payload,
      };

    case POST_FORM_ACTIONS.SET_SONG_URL:
      return {
        ...state,
        songURL: action.payload,
      };

    case POST_FORM_ACTIONS.SET_SONG_NAME:
      return {
        ...state,
        songName: action.payload,
      };

    case POST_FORM_ACTIONS.SET_SONG_ARTIST:
      return {
        ...state,
        songArtist: action.payload,
      };

    case POST_FORM_ACTIONS.SET_DESCRIPTION:
      return {
        ...state,
        description: action.payload,
      };

    case POST_FORM_ACTIONS.SET_SELECTED_GENRES:
      return {
        ...state,
        selectedGenres: action.payload,
      };

    case POST_FORM_ACTIONS.SET_SHOW_FULL_FORM:
      return {
        ...state,
        showFullForm: action.payload,
      };

    case POST_FORM_ACTIONS.SET_URL_VALID:
      return {
        ...state,
        isUrlValid: action.payload,
      };

    case POST_FORM_ACTIONS.SET_URL_ERROR:
      return {
        ...state,
        urlError: action.payload,
      };

    case POST_FORM_ACTIONS.SET_FETCHED_TITLE:
      return {
        ...state,
        fetchedTitle: action.payload,
      };

    case POST_FORM_ACTIONS.SET_FETCHED_THUMBNAIL:
      return {
        ...state,
        fetchedThumbnail: action.payload,
      };

    case POST_FORM_ACTIONS.SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: action.payload,
      };

    case POST_FORM_ACTIONS.CLEAR_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: {},
      };

    case POST_FORM_ACTIONS.RESET_FORM:
      return {
        ...initialState,
        isEditModalOpen: state.isEditModalOpen,
        isPostModalOpen: state.isPostModalOpen,
        isEditing: state.isEditing,
        postToEdit: state.postToEdit,
      };

    case POST_FORM_ACTIONS.LOAD_POST_FOR_EDIT:
      const post = action.payload;
      return {
        ...state,
        songName: post.songName || '',
        songArtist: post.songArtist || '',
        songURL: post.URL || '',
        description: post.description || '',
        selectedGenres: post.genres || [],
        fetchedTitle: post.songName || '',
        fetchedThumbnail: {
          default: { url: post.thumbnailURL },
          medium: { url: post.thumbnailURL },
          high: { url: post.thumbnailURL },
          standard: { url: post.thumbnailURL },
          maxres: { url: post.thumbnailURL },
        },
        showFullForm: true,
        isUrlValid: true,
        isEditing: true,
        postToEdit: post,
      };

    default:
      logger.warn('PostFormContext: Unknown action type:', action.type);
      return state;
  }
};

// Create the context
const PostFormContext = createContext();

// Provider component
export const PostFormProvider = ({ children }) => {
  const [state, dispatch] = useReducer(postFormReducer, initialState);

  // Memoized action creators for better performance
  const openEditModal = useCallback((post) => {
    if (process.env.REACT_APP_STRIKE_MODE === 'true') return;
    logger.debug('PostFormContext: Opening edit modal for post:', post?.id);
    dispatch({ type: POST_FORM_ACTIONS.OPEN_EDIT_MODAL, payload: post });
  }, []);

  const closeEditModal = useCallback(() => {
    logger.debug('PostFormContext: Closing edit modal');
    dispatch({ type: POST_FORM_ACTIONS.CLOSE_EDIT_MODAL });
  }, []);

  const openPostModal = useCallback(() => {
    if (process.env.REACT_APP_STRIKE_MODE === 'true') return;
    logger.debug('PostFormContext: Opening post modal');
    dispatch({ type: POST_FORM_ACTIONS.OPEN_POST_MODAL });
  }, []);

  const closePostModal = useCallback(() => {
    logger.debug('PostFormContext: Closing post modal');
    dispatch({ type: POST_FORM_ACTIONS.CLOSE_POST_MODAL });
  }, []);

  const setPostingState = useCallback((isPosting) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_POSTING_STATE, payload: isPosting });
  }, []);

  const setSongURL = useCallback((url) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_SONG_URL, payload: url });
  }, []);

  const setSongName = useCallback((name) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_SONG_NAME, payload: name });
  }, []);

  const setSongArtist = useCallback((artist) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_SONG_ARTIST, payload: artist });
  }, []);

  const setDescription = useCallback((description) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_DESCRIPTION, payload: description });
  }, []);

  const setSelectedGenres = useCallback((genres) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_SELECTED_GENRES, payload: genres });
  }, []);

  const setShowFullForm = useCallback((show) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_SHOW_FULL_FORM, payload: show });
  }, []);

  const setUrlValid = useCallback((isValid) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_URL_VALID, payload: isValid });
  }, []);

  const setUrlError = useCallback((error) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_URL_ERROR, payload: error });
  }, []);

  const setFetchedTitle = useCallback((title) => {
    dispatch({ type: POST_FORM_ACTIONS.SET_FETCHED_TITLE, payload: title });
  }, []);

  const setFetchedThumbnail = useCallback((thumbnail) => {
    dispatch({
      type: POST_FORM_ACTIONS.SET_FETCHED_THUMBNAIL,
      payload: thumbnail,
    });
  }, []);

  const setValidationErrors = useCallback((errors) => {
    dispatch({
      type: POST_FORM_ACTIONS.SET_VALIDATION_ERRORS,
      payload: errors,
    });
  }, []);

  const clearValidationErrors = useCallback(() => {
    dispatch({ type: POST_FORM_ACTIONS.CLEAR_VALIDATION_ERRORS });
  }, []);

  const resetForm = useCallback(() => {
    logger.debug('PostFormContext: Resetting form');
    dispatch({ type: POST_FORM_ACTIONS.RESET_FORM });
  }, []);

  const loadPostForEdit = useCallback((post) => {
    logger.debug('PostFormContext: Loading post for edit:', post?.id);
    dispatch({ type: POST_FORM_ACTIONS.LOAD_POST_FOR_EDIT, payload: post });
  }, []);

  // Context value with state and actions
  const contextValue = {
    // State
    ...state,

    // Actions
    openEditModal,
    closeEditModal,
    openPostModal,
    closePostModal,
    setPostingState,
    setSongURL,
    setSongName,
    setSongArtist,
    setDescription,
    setSelectedGenres,
    setShowFullForm,
    setUrlValid,
    setUrlError,
    setFetchedTitle,
    setFetchedThumbnail,
    setValidationErrors,
    clearValidationErrors,
    resetForm,
    loadPostForEdit,
  };

  return (
    <PostFormContext.Provider value={contextValue}>
      {children}
    </PostFormContext.Provider>
  );
};

// Custom hook to use the context
export const usePostForm = () => {
  const context = useContext(PostFormContext);
  if (!context) {
    throw new Error('usePostForm must be used within a PostFormProvider');
  }
  return context;
};

export default PostFormContext;
